import { trpc } from "../trpc";
import { OrganizationZaps } from "./useQueryOrganizationZaps";
import { StorageZap } from "./useQueryStorageZaps";

interface MutationInput {
  zapId: string;
  enable: boolean;
}

interface Input {
  folderId?: string; // used for cache invalidation
}

export function useToggleZap(input?: Input) {
  const { folderId } = input || {};

  const utils = trpc.useUtils();

  // const storageKey = getQueryKey(trpc.zap.storageZaps, undefined, "any");
  // const orgKey = getQueryKey(trpc.zap.organizationZaps, undefined, "any");
  // const searchKey = getQueryKey(trpc.zap.searchZaps, undefined, "any");

  const handleMutation = () => {
    // Update Zap under storageZaps key (search results view)
    utils.zap.storageZaps.cancel();

    // Update Zap under organizationZaps key (single folder view)
    utils.zap.organizationZaps.cancel();
  };

  const handleSettled = (zap: any) => {
    const zapId = zap?.zapId || zap?.id || "";

    // Invalidate storageZaps key to refetch data
    utils.zap.storageZaps.invalidate();
    // Invalidate organizationZaps key to refetch data
    utils.zap.organizationZaps.invalidate();
    // Invalidate search Zaps to get most up to date last_modified data
    utils.zap.searchZaps.invalidate();

    if (folderId) {
      // Invalidate folder assets for MPFs
      utils.asset.getHydratedFolderAssets.invalidate({ folderId });
    }

    // Refetch project Zap if it exists
    if (zapId) {
      utils.zap.get.invalidate({ id: zapId });
    }
  };

  const handleSuccess = ({ zapId, enable }: MutationInput) => {
    utils.zap.storageZaps.setData(
      { ids: [Number(zapId)] },
      (old: Record<string, StorageZap> | undefined) => {
        if (old?.[zapId]) {
          // this needs to return a new object ref for RQ to detect the change
          return {
            ...old,
            [zapId]: {
              ...old[zapId],
              isEnabled: enable,
              updatedAt: new Date().toISOString(),
              ...(enable
                ? { lastEnabledAt: new Date().toISOString() }
                : { lastDisabledAt: new Date().toISOString() }),
            },
          };
        }
        return old;
      },
    );

    if (folderId) {
      utils.zap.organizationZaps.setData(
        { folderId },
        (old: OrganizationZaps | undefined) => {
          if (old?.results?.find((zap) => zap.id === zapId)) {
            // this needs to return a new object ref for RQ to detect the change
            return {
              ...old,
              results: old.results.map((zap) => {
                if (zap.id === zapId) {
                  return {
                    ...zap,
                    isEnabled: enable,
                    updatedAt: new Date().toISOString(),
                    ...(enable
                      ? { lastEnabledAt: new Date().toISOString() }
                      : { lastDisabledAt: new Date().toISOString() }),
                  };
                }
                return zap;
              }),
            };
          }
          return old;
        },
      );
    }
  };

  return {
    handleMutation,
    handleSettled,
    handleSuccess,
  };
}
